/**
 * Testing-Mode simulated Central role override — UI-only.
 *
 * Central's testing-mode API stores the override server-side but does NOT
 * re-mint the simulating user's JWT or change what their own
 * switch-context response returns. So from the simulator's POV,
 * `bootContext.currentWorkspace.role` keeps the tester's REAL role.
 * That cascades into the sidebar, settings, role badge, and
 * useAppRole() — none of which would reflect the simulated role.
 *
 * This module is a tiny sessionStorage-backed mirror. BootGate applies
 * it to the boot context before dispatching to Redux, so every existing
 * reader sees the simulated role through its normal data path with no
 * call-site changes.
 *
 * Backend remains authoritative on every API call. If the tester tries
 * to take an action the real user can't do, the server still 403s.
 *
 * Cleared by:
 *   - Testing Mode → Clear button
 *   - logout
 *   - opening a new browser session
 */

export type SimulatedCentralRole = 'owner' | 'admin' | 'member' | 'viewer';

const STORAGE_KEY = 'beheard_test_central_role';

const VALID: readonly SimulatedCentralRole[] = ['owner', 'admin', 'member', 'viewer'];

/**
 * Testing-Mode overrides are a DEV-BUILD-ONLY affordance.
 *
 * The Testing Mode panel is gated on Central-asserted `platformInternal`, so
 * only staff can legitimately write these keys — but the READER used to ship
 * to production, where anyone could set the key in their own browser and get
 * an owner-shaped UI. No session is minted and Central still authorises every
 * API call, so this was cosmetic escalation rather than an auth bypass; it
 * still has no business existing in a production bundle.
 *
 * Gating the accessors (rather than each call site) means every consumer —
 * BootGate, SettingsPageV2, the panel — is covered by one check, and a new
 * call site cannot reintroduce the exposure.
 *
 * `import.meta.env.DEV` is statically replaced by Vite at build time, so in a
 * production bundle these bodies fold to constants and the simulation code is
 * dead. Clearing is deliberately NOT gated: it must always be possible to
 * remove a stale key.
 */
function simulationEnabled(): boolean {
  return Boolean(import.meta.env.DEV);
}

export function getSimulatedCentralRole(): SimulatedCentralRole | null {
  if (!simulationEnabled()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return (VALID as readonly string[]).includes(raw) ? (raw as SimulatedCentralRole) : null;
  } catch {
    return null;
  }
}

export function setSimulatedCentralRole(role: SimulatedCentralRole | null): void {
  // Clearing stays available in every build; setting does not.
  if (role !== null && !simulationEnabled()) return;
  try {
    if (role === null) sessionStorage.removeItem(STORAGE_KEY);
    else if ((VALID as readonly string[]).includes(role)) sessionStorage.setItem(STORAGE_KEY, role);
  } catch {
    // ignore storage errors (private mode, quota) — override simply won't persist
  }
}

export function clearSimulatedCentralRole(): void {
  setSimulatedCentralRole(null);
}
